import {
  getAuthorization,
  getCompanyId,
  setAuthorization,
} from "../Functions/AuthFunction";
import {
  ref,
  get,
  query,
  orderByChild,
  equalTo,
  update,
  set,
} from "firebase/database";
import { storage, db } from "./firebase";
import { v4 as uuidv4 } from "uuid";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import bcrypt from "bcryptjs";
import { sendOTP } from "./mailbox";

const adminsRootPath = "contacthq/admins";

export const registerAdmin = async (payload) => {
  let data =
    payload instanceof FormData
      ? Object.fromEntries(payload.entries())
      : payload;
  const { email, password, name, role } = data;
  if (!email || !password || !name) {
    return {
      success: false,
      message:
        "Essential administrative fields (Email, Password, Name) are missing.",
    };
  }

  try {
    const adminsRef = ref(db, adminsRootPath);
    const emailCheck = query(adminsRef, orderByChild("email"), equalTo(email));
    const emailSnap = await get(emailCheck);
    if (emailSnap.exists()) {
      return {
        success: false,
        message: "This administrative email is already provisioned.",
      };
    }
    const adminId = uuidv4();
    const token = uuidv4();
    const hashedPassword = bcrypt.hashSync(password, 10);
    let profilePicURL = "";
    if (
      data.profilePic &&
      data.profilePic instanceof File &&
      data.profilePic.size > 0
    ) {
      if (data.profilePic.size <= 2 * 1024 * 1024) {
        const picRef = storageRef(
          storage,
          `${adminsRootPath}/${adminId}/profile`,
        );
        await uploadBytes(picRef, data.profilePic);
        profilePicURL = await getDownloadURL(picRef);
      }
    }
    const now = new Date();
    const newAdmin = {
      ...data,
      adminId,
      password: hashedPassword,
      profilePicURL,
      token,
      status: false,
      isAdmin: true,
      role: role || "Chief Administrator",
      createdAt: now,
    };
    await set(ref(db, `${adminsRootPath}/${adminId}`), newAdmin);
    return {
      success: true,
      message:
        "Registration successful! Your account is pending approval by the Master Admin. You will be able to log in once access is granted.",
    };
  } catch (error) {
    console.error("Admin Provisioning Error:", error);
    return {
      success: false,
      message: "Initialization failed due to a server error.",
    };
  }
};
export const verifyOTP = async (email, otp) => {
  const adminsRootPath = "contacthq/admins";
  const adminsRef = ref(db, adminsRootPath);
  try {
    const emailQuery = query(adminsRef, orderByChild("email"), equalTo(email));
    const snapshot = await get(emailQuery);
    
    if (!snapshot.exists()) {
      return { success: false, message: "Administrative account not found." };
    }
    
    const adminId = Object.keys(snapshot.val())[0];
    const adminData = snapshot.val()[adminId];

    if (!adminData.otp || adminData.otp !== otp) {
      return { success: false, message: "Invalid OTP code." };
    }
    
    const now = new Date();
    const expiry = new Date(adminData.otpExpiry);
    if (now > expiry) {
      return { success: false, message: "OTP has expired. Please request a new one." };
    }

    const updates = {};
    // Note: status stays false for Master Admin approval
    updates[`${adminsRootPath}/${adminId}/otp`] = null;
    updates[`${adminsRootPath}/${adminId}/otpExpiry`] = null;
    updates[`${adminsRootPath}/${adminId}/isEmailVerified`] = true; // New flag for email status
    
    await update(ref(db), updates);

    let sendData = {
      adminId: adminData.adminId,
      name: adminData.name,
      email: adminData.email,
      role: adminData.role || "Chief Administrator",
      token: adminData.token,
      status: adminData.status, // Still false until Master Admin approval
    };

    return {
      success: true,
      message: "Email verified successfully! Access is pending Master Admin approval.",
      data: sendData,
    };
  } catch (error) {
    console.error("Verify Admin OTP Failed:", error);
    return { success: false, message: "Verification failed. Please try again." };
  }
};
export const resendOTP = async (email) => {
  const adminsRootPath = "contacthq/admins";
  const adminsRef = ref(db, adminsRootPath);
  try {
    const emailQuery = query(adminsRef, orderByChild("email"), equalTo(email));
    const snapshot = await get(emailQuery);
    
    if (!snapshot.exists()) {
      return { success: false, message: "Administrative account not found." };
    }
    
    const adminId = Object.keys(snapshot.val())[0];
    const adminData = snapshot.val()[adminId];
    
    const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
    const newExpiry = new Date(Date.now() + 15 * 60 * 1000).toISOString();
    
    const updates = {};
    updates[`${adminsRootPath}/${adminId}/otp`] = newOtp;
    updates[`${adminsRootPath}/${adminId}/otpExpiry`] = newExpiry;
    
    await update(ref(db), updates);
    await sendOTP({
      name: adminData.name,
      email: adminData.email,
      otp: newOtp,
      otpExpiry: newExpiry,
    });
    
    return { success: true, message: "New administrative code sent!" };
  } catch (error) {
    console.error("Resend Admin OTP Error:", error);
    return { success: false, message: "Failed to resend code." };
  }
};
export const loginAdmin = async ({ email, password }) => {
  if (!email || !password) {
    return { success: false, message: "Email and password are required" };
  }

  try {
    const adminsRef = ref(db, "contacthq/admins"); // Targeting the administrative node
    const adminQuery = query(adminsRef, orderByChild("email"), equalTo(email));

    const adminSnapshot = await get(adminQuery);

    if (!adminSnapshot.exists()) {
      return {
        success: false,
        message: "No administrative account found with this email.",
      };
    }

    const admins = adminSnapshot.val();
    const adminKey = Object.keys(admins)[0];
    const admin = admins[adminKey];

    // 1. Verify Master Access Key (Password)
    const isPasswordValid = bcrypt.compareSync(password, admin.password);
    if (!isPasswordValid) {
      return { success: false, message: "Incorrect password!" };
    }

    // 2. Check Administrative Approval Status
    // In your workflow, status is false by default and requires Master Admin approval.
    if (admin.status === false) {
      return {
        success: false,
        message:
          "Your account is pending approval by the Master Admin. Please try again once access is granted.",
        isPendingApproval: true,
      };
    }

    // 3. Update Session Metadata
    const now = new Date().toISOString();
    const newToken = uuidv4();

    await update(ref(db, `contacthq/admins/${adminKey}`), {
      lastLoginAt: now,
      token: newToken,
    });

    // 4. Structure Administrative Data Payload
    let adminData = {
      adminId: admin.adminId,
      name: admin.name,
      email: admin.email,
      mobile: admin.mobile,
      role: admin.role || "Chief Administrator",
      profilePicURL: admin.profilePicURL,
      token: newToken,
      status: admin.status,
      lastLoginAt: now,
    };

    // Clean and set Auth headers
    const cleanId = admin.adminId;
    setAuthorization(newToken, cleanId);

    return {
      success: true,
      message: "Admin login successful!",
      data: adminData,
    };
  } catch (error) {
    console.error("🔥 Admin Login failed:", error);
    return {
      success: false,
      message:
        "An error occurred during admin authentication. Please try again.",
    };
  }
};
export const requestPasswordReset = async (email) => {
  const adminsRootPath = "contacthq/admins";
  const adminsRef = ref(db, adminsRootPath);
  try {
    const emailQuery = query(adminsRef, orderByChild("email"), equalTo(email));
    const snapshot = await get(emailQuery);
    
    if (!snapshot.exists()) {
      return { success: false, message: "No administrative account found with this email." };
    }
    
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    const expiryTime = new Date(Date.now() + 15 * 60 * 1000).toISOString();
    const adminId = Object.keys(snapshot.val())[0];
    const adminData = snapshot.val()[adminId];
    
    await update(ref(db, `${adminsRootPath}/${adminId}`), {
      otp: otp,
      otpExpiry: expiryTime,
    });
    
    const mailResult = await sendOTP({
      email: adminData.email,
      name: adminData.name,
      otp: otp,
      otpExpiry: expiryTime,
    });
    
    if (!mailResult.success) {
      throw new Error("Could not send recovery email.");
    }
    
    return {
      success: true,
      message: "A reset code has been sent to your administrative email.",
      data: { email: adminData.email },
    };
  } catch (error) {
    console.error("🔥 Admin Password Reset Request Failed:", error);
    return { success: false, message: error.message || "Failed to process request." };
  }
};
export const resetPassword = async (email, newPassword) => {
  const adminsRootPath = "contacthq/admins";
  const adminsRef = ref(db, adminsRootPath);
  try {
    const emailQuery = query(adminsRef, orderByChild("email"), equalTo(email));
    const snapshot = await get(emailQuery);
    
    if (!snapshot.exists()) {
      return { success: false, message: "Administrative account not found." };
    }
    
    const adminId = Object.keys(snapshot.val())[0];
    const hashedPassword = bcrypt.hashSync(newPassword, 10);
    const newToken = uuidv4();
    
    await update(ref(db, `${adminsRootPath}/${adminId}`), {
      password: hashedPassword,
      token: newToken,
      otp: null,
      otpExpiry: null,
      lastPasswordChange: new Date().toISOString(),
    });
    
    return {
      success: true,
      message: "Master key reset successfully. Please log in with your new credentials.",
    };
  } catch (error) {
    console.error("🔥 Reset Admin Password Failed:", error);
    return { success: false, message: "An error occurred while updating the master key." };
  }
};
export const verifyAuth = async () => {
  const adminId = getCompanyId(); // Using existing helper to get current ID
  const token = getAuthorization(); // Using existing helper to get current token

  if (!adminId || !token) {
    return { success: false, error: "Missing Admin ID or Session Token" };
  }

  try {
    const adminRef = ref(db, `contacthq/admins/${adminId}`);
    const snapshot = await get(adminRef);

    if (!snapshot.exists()) {
      return { success: false, error: "Administrative record not found." };
    }

    const adminData = snapshot.val();

    // 1. Session Token Validation
    if (adminData.token !== token) {
      return {
        success: false,
        error: "Unauthorized: Session expired or invalid.",
      };
    }

    // 2. Access/Approval Validation
    // Status must be true (approved by Master Admin) to maintain an active session
    if (!adminData.status) {
      return { success: false, error: "Access Denied: Account is pending Master Admin approval." };
    }

    return {
      success: true,
      adminData: { ...adminData, adminId },
    };
  } catch (error) {
    console.error("🔥 Admin Auth verification failed:", error);
    return { success: false, error: "Administrative authentication service error." };
  }
};
export const verifyCompanyEmail = async (token) => {
  const adminsRootPath = "contacthq/admins";
  const adminsRef = ref(db, adminsRootPath);
  
  try {
    const tokenQuery = query(
      adminsRef,
      orderByChild("token"),
      equalTo(token),
    );
    
    const snapshot = await get(tokenQuery);

    if (!snapshot.exists()) {
      return {
        success: false,
        message: "Invalid or expired administrative verification link.",
      };
    }

    const adminId = Object.keys(snapshot.val())[0];
    const adminData = snapshot.val()[adminId];

    // If already verified, set local auth and return
    if (adminData.isEmailVerified === true) {
      const cleanId = String(adminId).replace(/"/g, "");
      setAuthorization(token, cleanId);
      return { success: true, message: "Administrative email already verified." };
    }

    const adminRef = ref(db, `${adminsRootPath}/${adminId}`);
    
    // Update verification timestamp and flag
    await update(adminRef, {
      isEmailVerified: true, // Specific flag for email verification
      emailVerifiedAt: new Date().toISOString(),
    });

    const cleanId = String(adminId).replace(/"/g, "");
    setAuthorization(token, cleanId);

    return {
      success: true,
      message: "Admin email verified! Access is now pending Master Admin approval.",
      data: { ...adminData, isEmailVerified: true, adminId: cleanId },
    };
  } catch (error) {
    console.error("🔥 Admin Verification failed:", error);
    return {
      success: false,
      message: "Verification failed. Please try again.",
    };
  }
};
export const subscriptionCheckAction = async (companyData) => {
  try {
    const subscription = companyData.subscription || {};
    const expiresAt = subscription.expiresAt;
    const companyId = companyData.companyId;
    const formLimit = parseInt(subscription.formLimit) || 1;
    const formsRef = ref(db, "contacthq/forms");
    const formsQuery = query(
      formsRef,
      orderByChild("companyId"),
      equalTo(companyId),
    );
    const formsSnapshot = await get(formsQuery);
    if (!formsSnapshot.exists()) {
      return { success: true, expired: false, message: "No forms found." };
    }
    const allForms = [];
    formsSnapshot.forEach((child) => {
      allForms.push({ id: child.key, ...child.val() });
    });
    const updates = {};
    let hasUpdates = false;
    if (expiresAt && new Date(expiresAt) < new Date()) {
      let pausedCount = 0;
      allForms.forEach((form) => {
        if (form.status === "published" || form.status === "Active") {
          updates[`contacthq/forms/${form.id}/status`] = "Paused";
          hasUpdates = true;
          pausedCount++;
        }
      });
      if (hasUpdates) await update(ref(db), updates);
      return {
        success: true,
        expired: true,
        message: `Subscription expired. ${pausedCount} forms paused.`,
      };
    }
    const activeForms = allForms.filter(
      (f) => f.status === "published" || f.status === "Active",
    );
    if (activeForms.length > formLimit) {
      activeForms.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const formsToPause = activeForms.slice(formLimit);
      formsToPause.forEach((form) => {
        updates[`contacthq/forms/${form.id}/status`] = "Paused";
        hasUpdates = true;
      });
      if (hasUpdates) {
        await update(ref(db), updates);
        return {
          success: true,
          expired: false,
          limitEnforced: true,
          message: `Plan limit exceeded. ${formsToPause.length} oldest forms were auto-paused.`,
        };
      }
    }
    return {
      success: true,
      expired: false,
      message: "Subscription is active.",
    };
  } catch (error) {
    console.error("Subscription Check Error:", error);
    return {
      success: false,
      expired: false,
      message: error.message || "Failed to check subscription status.",
    };
  }
};
